import React, {useEffect, useMemo, useRef, useState} from "react";
import { useQuery, useSubscription } from "@apollo/client/react";
import isEqual from "lodash/isEqual";

import {ASSETS_COLLECTION_TABLE_QUERY, INVENTORY_ASSET_STOCKED} from "../constants/graphql";
import useMutation from "../hooks/useMutation";
import useNotification from "../hooks/notification";
import {addRecord, removeCollection, replaceRecord, updateCollectionQuery,} from "../components/TablePro/utils";
import {
  ADD_ASSET_IMAGE_MUTATION,
  ASSETS_BULK_REMOVE_MUTATION,
  BULK_CREATE_ASSET_IMAGES_MUTATION,
  CREATE_GROUPED_ASSET_MUTATION,
  DELETE_IMAGE_MUTATION,
  UPDATE_ASSET_MUTATION,
  UPDATE_IMAGE_MUTATION,
} from "../constants/graphql/mutations";

const NOTIFICATIONS = {
  assetCreated: "New Grouped Item was added",
  assetUpdated: "Grouped Item was updated",
  assetRemoved: "Grouped Item was removed",
  imagesUploaded: "Images have been uploaded",
};

const GroupedAssetsCollectionContext = React.createContext(null);

export const GroupedAssetsCollectionProvider = ({ children, variables = {}, query }) => {
  const mounted = useRef(false);
  const { notifyError, notifySuccess } = useNotification();
  const [loading, setLoading] = useState(true);
  const [loadingMutation, setLoadingMutation] = useState(false);
  const [collection, setCollection] = useState([]);
  const [metadata, setMetadata] = useState({});

  const [createAsset] = useMutation(CREATE_GROUPED_ASSET_MUTATION);
  const [updateAsset] = useMutation(UPDATE_ASSET_MUTATION);
  const [removeImage] = useMutation(DELETE_IMAGE_MUTATION);
  const [createImage] = useMutation(ADD_ASSET_IMAGE_MUTATION);
  const [updateImage] = useMutation(UPDATE_IMAGE_MUTATION);
  const [bulkRemoveAssets] = useMutation(ASSETS_BULK_REMOVE_MUTATION);
  const [bulkCreateImages] = useMutation(BULK_CREATE_ASSET_IMAGES_MUTATION);

  const [search, setSearch] = useState(variables.search);
  const [limit, setLimit] = useState(variables.limit || 40);

  const collectionFilter = {
    ...variables,
    search: search,
    limit,
    page: variables.page || 1,
    type: "GroupedAsset",
  };

  const { fetchMore, refetch, data: queryData } = useQuery(ASSETS_COLLECTION_TABLE_QUERY, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: collectionFilter,
  });

  useEffect(() => {
    if (queryData) {
      onFetch(queryData);
    }
  }, [queryData]);

  const onFetch = ({ assetsCollection = {} } = {}) => {
    setCollection(assetsCollection?.collection || []);
    setMetadata(assetsCollection?.metadata || {});
    setLoading(false);
  };

  const handeRefetch = () => {
    setLoading(true);

    refetch(collectionFilter)
      .then(({ data }) => {
        onFetch(data);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    } else {
      handeRefetch();
    }
  }, [...Object.values(collectionFilter)]);

  const onLoadMore = () => {
    fetchMore({
      variables: { ...collectionFilter, page: metadata.currentPage + 1 },
      updateQuery: updateCollectionQuery("assetsCollection", {
        collection,
        metadata,
        onSuccess: onFetch,
        onFailure: () => {
          setLoading(false);
        },
      }),
    });
  };

  useSubscription(INVENTORY_ASSET_STOCKED, {
    onData: ({ data: { data: { inventoryAssetStocked } = {} } = {} }) => {
      const existingAsset = collection?.find(asset => asset.id === inventoryAssetStocked.id);

      if (!existingAsset) return;

      const newAsset = { ...existingAsset, ...inventoryAssetStocked };

      if (isEqual(Object.values(existingAsset).sort(), Object.values(newAsset).sort())) {
        return;
      }

      replaceRecord(setCollection, {
        id: inventoryAssetStocked.id,
        values: newAsset,
      });
    },
  });

  const handleUpdateAsset = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);

    updateAsset({
      variables,
      onSuccess: ({ asset }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: asset,
        });

        setLoadingMutation(false);
        notifySuccess(NOTIFICATIONS.assetUpdated);
        if (onSuccess) onSuccess({ asset });
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        if (onFailure) onFailure(errors);
      },
    });
  };

  const handleCreateAsset = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);

    return createAsset({
      variables: variables,
      onSuccess: ({ groupedAsset }) => {
        addRecord({ setCollection, setMetadata, record: groupedAsset });
        setLoadingMutation(false);
        notifySuccess(NOTIFICATIONS.assetCreated);
        if (onSuccess) onSuccess({ groupedAsset });
      },
      onFailure: errors => {
        setLoadingMutation(false);
        if (onFailure) onFailure(errors);
      },
    });
  };

  const handleRemoveImage = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);
    removeImage({
      variables,
      onSuccess: ({ asset }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: { images: asset.images, photo: asset.photo },
        });
        setLoadingMutation(false);
        onSuccess && onSuccess({ asset });
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleCreateImage = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);
    createImage({
      variables: { ...variables, default: true },
      onSuccess: ({ asset }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: { images: asset.images, photo: asset.photo },
        });
        setLoadingMutation(false);
        onSuccess && onSuccess({ asset });
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleBulkCreateImages = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);

    bulkCreateImages({
      variables,
      onSuccess: ({ asset, images }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: { images: asset.images, photo: asset.photo },
        });

        setLoadingMutation(false);
        onSuccess && onSuccess({ asset, images });
        notifySuccess(NOTIFICATIONS.imagesUploaded);
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleUpdateImage = ({ variables, onSuccess, onFailure }) => {
    setLoadingMutation(true);

    updateImage({
      variables,
      onSuccess: ({ asset }) => {
        replaceRecord(setCollection, {
          id: asset.id,
          values: { images: asset.images, photo: asset.photo },
        });
        setLoadingMutation(false);
        onSuccess && onSuccess({ asset });
      },
      onFailure: errors => {
        notifyError(errors);
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const handleBulkRemove = ({ variables, onSuccess, onFailure }) => {
    const { ids } = variables;
    setLoadingMutation(true);

    bulkRemoveAssets({
      variables,
      onSuccess: () => {
        removeCollection({ setCollection, setMetadata, ids });
        setLoadingMutation(false);
        onSuccess && onSuccess();
      },
      onFailure: errors => {
        setLoadingMutation(false);
        onFailure && onFailure(errors);
      },
    });
  };

  const collectionIds = useMemo(() => collection?.map(entity => entity.id) || [], [collection]);

  const removeAssetsFromCollection = ids => {
    removeCollection({ setCollection, setMetadata, ids });
  };

  const value = {
    // Collection
    loading,
    setLoading,
    collection,
    setCollection,
    collectionIds,
    handeRefetch,
    onLoadMore,
    search,
    setSearch,
    limit,
    setLimit,

    // Metadata
    setMetadata,
    metadata,
    ...metadata,
    totalIds: metadata.totalIds || [],

    // Mutation
    loadingMutation,
    setLoadingMutation,
    removeAssetsFromCollection,
    createAsset: handleCreateAsset,
    updateAsset: handleUpdateAsset,
    removeImage: handleRemoveImage,
    createImage: handleCreateImage,
    updateImage: handleUpdateImage,
    bulkRemoveAssets: handleBulkRemove,
    bulkCreateImages: handleBulkCreateImages,
  };

  return <GroupedAssetsCollectionContext.Provider value={value}>{children}</GroupedAssetsCollectionContext.Provider>;
};

export const useGroupedAssetsCollection = () => {
  const context = React.useContext(GroupedAssetsCollectionContext);
  if (!context) {
    throw new Error("useGroupedAssetsCollection must be used within a GroupedAssetsCollectionProvider");
  }
  return context;
};

